import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchTurnaround, type PendingReport, type ToDo, type TurnaroundRow } from "../../api/statisticsApi";
import { ReportState, ReportType } from "../../types/Rows";
import {
  Count, DaysPill, fmtDate, fmtDays, LINK, Message, OVERDUE_DAYS, Pill, ROW, Section, Segmented,
  shortEmail, StatCard, Table, TD, TH, useLimited, TabSkeleton, TableSkeleton } from "./statsUi";

const AGE_OPTIONS = [
  { label: "Ostatnie 90 dni", value: 90 },
  { label: "Wszystkie", value: 0 },
];

const MONTH_OPTIONS = [
  { label: "3 mies.", value: 3 },
  { label: "6 mies.", value: 6 },
  { label: "12 mies.", value: 12 },
];

const Email = ({ email }: { email: string }) => <span title={email}>{shortEmail(email)}</span>;

export const ToDoTab = ({
  data, isLoading, study, monitor,
}: { data?: ToDo; isLoading: boolean; study: string; monitor: string }) => {
  const [maxAge, setMaxAge] = useState(90);

  const filtered = useMemo(() => {
    if (!data) return null;
    const byStudy = <T extends { studyName: string }>(x: T) => !study || x.studyName === study;
    const byAge = <T extends { daysSince: number }>(x: T) => !maxAge || x.daysSince <= maxAge;
    return {
      visits: data.visitsWithoutReport
        .filter(byStudy).filter(byAge)
        .filter(v => !monitor || v.monitors.includes(monitor))
        .sort((a, b) => b.daysSince - a.daysSince),
      drafts: data.draftReports
        .filter(byStudy).filter(byAge)
        .filter(r => !monitor || r.author === monitor)
        .sort((a, b) => b.daysSince - a.daysSince),
      queue: data.reviewQueue
        .filter(byStudy)
        .filter(r => !monitor || r.author === monitor),
    };
  }, [data, study, monitor, maxAge]);

  if (isLoading)
    return (
      <TabSkeleton
        cards={["Wizyty odbyte bez raportu", "Raporty niewysłane do weryfikacji", "Czekają na weryfikację", "Do poprawy u monitora"]}
        sections={["Wizyty odbyte bez raportu", "Raporty niewysłane do weryfikacji", "Kolejka weryfikacji"]}
      />
    );
  if (!filtered) return <Message>Nie udało się pobrać danych</Message>;

  const { visits, drafts, queue } = filtered;
  const toVerify = queue.filter(r => r.state === ReportState.TO_VERIFY);
  const toCorrect = queue.filter(r => r.state === ReportState.TO_CORRECT);
  const over = (xs: { daysSince: number }[]) => xs.filter(x => x.daysSince > OVERDUE_DAYS).length;
  const overHint = (xs: { daysSince: number }[]) => `w tym ponad ${OVERDUE_DAYS} dni: ${over(xs)}`;

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Segmented<number> options={AGE_OPTIONS} value={maxAge} onChange={setMaxAge} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Wizyty odbyte bez raportu" value={visits.length} hint={overHint(visits)} tone="red" />
        <StatCard label="Raporty niewysłane do weryfikacji" value={drafts.length} hint={overHint(drafts)} tone="amber" />
        <StatCard label="Czekają na weryfikację" value={toVerify.length} hint={overHint(toVerify)} tone="amber" />
        <StatCard label="Do poprawy u monitora" value={toCorrect.length} hint={overHint(toCorrect)} tone="amber" />
      </div>

      <VisitsSection visits={visits} />
      <ReportsSection
        title="Raporty niewysłane do weryfikacji"
        description="Raporty „W trakcie” po dacie wizyty (raporty CM: od utworzenia). Dni liczone od końca wizyty."
        sinceLabel="Wizyta"
        reports={drafts}
      />
      <ReportsSection
        title="Kolejka weryfikacji"
        description="Raporty „Do weryfikacji” i „Do poprawy”. Dni liczone od wejścia w obecny status."
        sinceLabel="W statusie od"
        reports={queue}
        showState
      />
      <TurnaroundSection study={study} />
    </div>
  );
};

const VisitsSection = ({ visits }: { visits: ToDo["visitsWithoutReport"] }) => {
  const { visible, toggle } = useLimited(visits);
  return (
    <Section
      title="Wizyty odbyte bez raportu"
      description="Wizyty planowane, których termin minął, a nie mają połączonego raportu (w kalendarzu na czerwono). Jeśli raport istnieje, połącz go na stronie wizyty."
    >
      {visits.length === 0 ? <Message>Brak zaległych wizyt</Message> : (
        <>
          <Table head={<><TH>Wizyta</TH><TH>Badanie</TH><TH>Ośrodek</TH><TH>Data</TH><TH>Od wizyty</TH><TH>Osoby</TH></>}>
            {visible.map(v => (
              <tr key={v.visitId} className={ROW}>
                <TD><Link to={`/visit/${v.visitId}`} className={LINK}>{v.visitName || "Wizyta"}</Link></TD>
                <TD>{v.studyName}</TD>
                <TD>{v.resortName}</TD>
                <TD className="whitespace-nowrap">{fmtDate(v.visitDate)}</TD>
                <TD><DaysPill days={v.daysSince} /></TD>
                <TD className="text-[var(--sc-muted)]">
                  {v.monitors.map((m, i) => <span key={m}>{i > 0 && ", "}<Email email={m} /></span>)}
                </TD>
              </tr>
            ))}
          </Table>
          {toggle}
        </>
      )}
    </Section>
  );
};

const ReportsSection = ({
  title, description, sinceLabel, reports, showState,
}: { title: string; description: string; sinceLabel: string; reports: PendingReport[]; showState?: boolean }) => {
  const { visible, toggle } = useLimited(reports);
  return (
    <Section title={title} description={description}>
      {reports.length === 0 ? <Message>Brak raportów</Message> : (
        <>
          <Table head={<>
            <TH>Raport</TH>{showState && <TH>Status</TH>}<TH>Badanie</TH><TH>Ośrodek</TH><TH>Autor</TH>
            <TH>{sinceLabel}</TH><TH>Dni</TH>
          </>}>
            {visible.map(r => (
              <tr key={r.reportId} className={ROW}>
                <TD>
                  <Link to={`/reports/${r.reportId}`} className={LINK}>{r.reportName}</Link>
                  {r.reportType === ReportType.CENTRAL_MONITORING && <span className="ml-2"><Pill>CM</Pill></span>}
                </TD>
                {showState && (
                  <TD>{r.state === ReportState.TO_CORRECT
                    ? <Pill tone="red">Do poprawy</Pill>
                    : <Pill tone="amber">Do weryfikacji</Pill>}
                  </TD>
                )}
                <TD>{r.studyName}</TD>
                <TD>{r.resortName}</TD>
                <TD className="text-[var(--sc-muted)]"><Email email={r.author} /></TD>
                <TD className="whitespace-nowrap">{fmtDate(r.since)}</TD>
                <TD><DaysPill days={r.daysSince} /></TD>
              </tr>
            ))}
          </Table>
          {toggle}
        </>
      )}
    </Section>
  );
};

const pct = (n: number, of: number) => (of === 0 ? "" : ` (${Math.round((n * 100) / of)}%)`);

const TurnaroundTableRow = ({ r, bold }: { r: TurnaroundRow; bold?: boolean }) => (
  <tr className={ROW}>
    <TD className={bold ? "font-semibold" : undefined}>{r.studyName}</TD>
    <TD right>{r.visits}</TD>
    <TD right>{r.submitted}</TD>
    <TD right>{r.accepted}</TD>
    <TD right><Count n={r.returnedForCorrection} tone="amber" />{pct(r.returnedForCorrection, r.submitted)}</TD>
    <TD right>{fmtDays(r.medianDaysToSubmit)}</TD>
    <TD right>{fmtDays(r.medianDaysInReview)}</TD>
    <TD right className="font-medium">{fmtDays(r.medianDaysToAccept)}</TD>
  </tr>
);

const TurnaroundSection = ({ study }: { study: string }) => {
  const [months, setMonths] = useState(6);
  const { data, isLoading } = useQuery(["statistics-turnaround", months], () => fetchTurnaround(months));

  const rows = data ? data.byStudy.filter(r => !study || r.studyName === study) : [];
  return (
    <Section
      title="Terminowość raportów"
      description="Raporty z wizyt (bez CM), których wizyta skończyła się w wybranym okresie. Mediana w dniach kalendarzowych; daty z historii statusów (pierwsze wysłanie, pierwsza akceptacja)."
      actions={<Segmented<number> options={MONTH_OPTIONS} value={months} onChange={setMonths} />}
    >
      {isLoading ? <TableSkeleton rows={4} cols={8} /> : !data || data.total.visits === 0 ? <Message>Brak wizyt w tym okresie</Message> : (
        <Table head={<>
          <TH>Badanie</TH><TH right>Wizyty</TH><TH right>Wysłane</TH><TH right>Zaakceptowane</TH>
          <TH right>Zwrócone do poprawy</TH><TH right>Wizyta → wysłanie</TH><TH right>Weryfikacja</TH>
          <TH right>Wizyta → akceptacja</TH>
        </>}>
          {!study && <TurnaroundTableRow r={data.total} bold />}
          {rows.map(r => <TurnaroundTableRow key={r.studyId} r={r} />)}
        </Table>
      )}
    </Section>
  );
};
